import { QueryClient } from '@tanstack/react-query';
import i18n, { t } from 'i18next';
import { SetRequired } from 'type-fest';

import { DEFAULT_HOURS, extractIntervalsFromHours } from '@/components/pages/BusinessHours/utils';
import { DEFAULT_INTEGRATION_CONFIG } from '@/components/pages/Integration/schemas';
import { API as brainsApi, brainCache } from '@/hooks/useBrains';
import { API as businessHoursApi } from '@/hooks/useBusinessHours';
import { API as deskApi, deskCache } from '@/hooks/useDesks';
import { API as integrationsApi, integrationCache } from '@/hooks/useIntegrations';
import { API as userPreferencesApi } from '@/hooks/usePreferences';
import { API as templatesApi } from '@/hooks/useTemplates';
import { onRuleCreated, API as rulesApi } from '@/modules/rules/hooks/useRules';
import { Desk } from '@/shared/types/desk';
import { Integration, IntegrationId, IntegrationType } from '@/shared/types/integration';
import { ActionType, Rule } from '@/shared/types/rules';
import { extractNames, generateUniqueName } from '@/util/util';

/**
 * Creates a desk and default business hours if there isn't one.
 */
export const setupDeskIfRequired = async ({
  newAccount = false,
}: {
  newAccount?: boolean;
}): Promise<Desk> => {
  const name = i18n.t('onboarding:environment_name');
  const { desks } = await deskApi.listDesks();
  let desk = desks.find((d) => d.name === name);
  // 1. Create a desk if it doesn't exist
  if (!desk) {
    desk = await deskApi.createDesk({
      name,
      is_service_desk: true,
      description: i18n.t(newAccount ? 'account.created' : 'onboarding:environment_description', {
        0: new Date(),
      }),
    });
  }

  // 2. Create business hours if they do not exist
  const { business_hours } = await businessHoursApi.listBusinessHours(desk.desk_id);

  if (business_hours?.length === 0) {
    // Create default business hours
    await businessHoursApi.addBusinessHour(desk.desk_id, {
      name: i18n.t('common:business_hours.default'),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      is_default: true,
      intervals: extractIntervalsFromHours([DEFAULT_HOURS]),
      holidays: [],
    });
  }

  return desk;
};

/**
 * Creates integrations if there aren't there. It uses the `desk_id` and `type`
 * provided as parameter
 */
export const setupIntegrationsIfRequired = async (
  params: SetRequired<Partial<Integration>, 'desk_id'> & { types?: IntegrationType[] }
): Promise<Integration[]> => {
  const { desk_id, types, ...restParams } = params;
  const uniqueTypes = [...new Set(types)];
  const { integrations } = await integrationsApi.listIntegrations(desk_id);
  const integrationPromises = uniqueTypes.map(async (t) => {
    let integration = integrations.find((i) => i.type === t);
    if (!integration) {
      const defaultConfig = DEFAULT_INTEGRATION_CONFIG[t]?.config || {};
      integration = await integrationsApi.createIntegration({
        ...restParams,
        desk_id,
        type: t,
        name: t,
        active: t === IntegrationType.WEB,
        config: defaultConfig,
      });
    }
    return integration;
  });

  return await Promise.all(integrationPromises);
};

/**
 * Creates an integration if there isn't one there. It uses the `desk_id` and `type`
 * provided as parameter
 */
export const setupIntegrationIfRequired = async (
  params: SetRequired<Partial<Integration>, 'desk_id' | 'type'>
): Promise<Integration> => {
  const { integrations } = await integrationsApi.listIntegrations(params.desk_id);

  let integration = integrations.find((i) => i.type === params.type);
  if (!integration) {
    integration = await integrationsApi.createIntegration({ ...params, name: params.type });
  }
  return integration;
};

export const updateIntegration = async <T extends Integration>(
  params: IntegrationId<T>
): Promise<T> => {
  let integration = await integrationsApi.getIntegration<T>(params.desk_id, params.integration_id);

  integration = await integrationsApi.updateIntegration<T>({
    ...integration,
    config: { ...integration.config, ...params.config },
  });

  return integration;
};

/**
 * Creates a default rule to assign conversations to a brain if there is not one
 */
export const setupRuleIfRequired = async (deskId: string, brainId: string): Promise<Rule> => {
  const brain_parent_id = brainId;
  const brain_version = 0;

  const { rules } = await rulesApi.listRules(deskId);

  // If there is a rule that assigns to this brain, make sure it's the first rule
  const existingRule = rules.find((rule) =>
    rule.actions.some(
      (action) =>
        action.type === ActionType.ASSIGN_BRAIN && action.brain_parent_id === brain_parent_id
    )
  );
  // If the existing rule is already the first rule, nothing more to do
  if (existingRule && rules[0].rule_id === existingRule.rule_id) {
    return;
  }

  // If the existing rule is not the first rule, move it to the top
  if (existingRule) {
    await rulesApi.reorderRule(deskId, existingRule.rule_id, 1);
    return;
  }

  // otherwise, create a new rule
  const newRule = await rulesApi.addRule(deskId, {
    name: generateUniqueName(i18n.t('onboarding:rule_name'), extractNames(rules)),
    description: '',
    status: 'active',
    triggers: [{ type: 'first_message' }],
    condition: { conditions: [], operator: 'or' },
    actions: [{ brain_parent_id, type: ActionType.ASSIGN_BRAIN, brain_version }],
    metadata: { source: 'onboarding' },
  });

  // and move it to the top
  return await rulesApi.reorderRule(deskId, newRule.rule_id, 1);
};

// this is used only when creating a new account
export const initDeskBrainIntegration = async ({
  queryClient,
  language,
  accountId,
}: { queryClient?: QueryClient; language?: string; accountId?: string } = {}) => {
  try {
    const desk = await setupDeskIfRequired({ newAccount: true });

    if (queryClient) {
      deskCache.created(queryClient, { ...desk, account_id: accountId });
    }

    const integr = await setupIntegrationsIfRequired({
      desk_id: desk.desk_id,
      types: [IntegrationType.WEB],
    });

    if (queryClient) {
      for (const integration of integr) {
        integrationCache.created(queryClient, integration);
      }
    }

    const userPreferences = await userPreferencesApi.getUserPreferences();
    const lang = userPreferences?.preferences?.language as string;
    const brain = await brainsApi.getTemplate(language || lang || 'en');

    const createdBrain = await templatesApi.createBrain({
      ...brain,
      brain_id: 'playground',
      name: t('onboarding:first_agent'),
    });

    if (queryClient) {
      brainCache.created(queryClient, createdBrain);
    }

    const rule = await setupRuleIfRequired(desk.desk_id, createdBrain.brain_id);

    if (queryClient) {
      onRuleCreated(queryClient, rule, desk.desk_id);
    }
  } catch (error) {
    console.error('Error initializing brain with template:', error);
    throw error;
  }
};

// helper function to create language options
export const createLangOptions = (sourceArray, languageByCountryCode, t) => {
  return sourceArray.map((i) => ({
    value: i.toLowerCase(),
    label: t(`languages:${i.toLowerCase()}`),
  }));
};

/**
 * Extracts and formats company name from email domain
 * @param email - User's email address
 * @returns Formatted company name or empty string for personal emails
 */
export const extractCompanyFromEmail = (email: string | undefined): string => {
  if (!email || !email.includes('@')) {
    return '';
  }

  // List of common personal email domains to exclude
  const personalDomains = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'icloud.com',
    'protonmail.com',
    'mail.com',
    'yandex.com',
    'zoho.com',
    'gmx.com',
    'fastmail.com',
    'tutanota.com',
    'pm.me',
    'proton.me',
  ];

  // Extract domain from email
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) {
    return '';
  }

  // Check if it's a personal email domain
  if (personalDomains.some((personal) => domain.includes(personal))) {
    return '';
  }

  // Extract company name from domain (remove .com, .org, etc.)
  const companyName = domain.split('.')[0];

  // Capitalize first letter of each word (handle cases like "my-company")
  return companyName
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get placeholder text based on extracted company or default
 */
export const getCompanyPlaceholder = (email: string | undefined): string => {
  const extracted = extractCompanyFromEmail(email);
  return extracted || 'Your Company';
};

/**
 * Generates training steps based on knowledge sources
 * @param knowledgeSources - The knowledge sources configuration
 * @returns Array of training steps
 */
export const generateTrainingSteps = (knowledgeSources?: {
  website?: string;
  websiteKnowledge?: { type: 'website' | 'zendesk_kb'; url: string; subdomain?: string };
  files?: Array<{ name: string }>;
}) => {
  const steps = [
    {
      id: 'desk',
      label: 'Setting up environment',
      icon: 'EnvironmentIcon',
      status: 'pending' as const,
    },
    { id: 'brain', label: 'Creating AI Agent', icon: 'BrainIcon', status: 'pending' as const },
  ];

  const hasWebsite = Boolean(knowledgeSources?.website);
  const hasFiles = Boolean(knowledgeSources?.files?.length);

  // Add knowledge base steps if there are sources
  if (hasWebsite || hasFiles) {
    steps.push({
      id: 'collection',
      label: 'Setting up knowledge base',
      icon: 'DatabaseIcon',
      status: 'pending' as const,
    });

    if (hasWebsite) {
      const websiteKnowledge = knowledgeSources?.websiteKnowledge;

      if (websiteKnowledge?.type === 'zendesk_kb') {
        // Show Zendesk-specific step
        steps.push({
          id: 'zendesk',
          label: `Connecting Zendesk (${websiteKnowledge.subdomain})`,
          icon: 'ZendeskIcon',
          status: 'pending' as const,
        });
      } else {
        // Show regular website step
        steps.push({
          id: 'website',
          label: 'Processing website content',
          icon: 'GlobeIcon',
          status: 'pending' as const,
        });
      }
    }

    // Add individual file processing steps
    knowledgeSources?.files?.forEach((file, idx) => {
      steps.push({
        id: `file-${idx}`,
        label: `Processing ${file.name}`,
        icon: 'FileTextIcon',
        status: 'pending' as const,
      });
    });
  }

  // Always add final training step
  steps.push({
    id: 'training',
    label: 'Training AI model',
    icon: 'SparklesIcon',
    status: 'pending' as const,
  });

  // Always add rule setup step
  steps.push({
    id: 'rule',
    label: 'Assigning rules',
    icon: 'RuleIcon',
    status: 'pending' as const,
  });

  return steps;
};
